import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Calendar, Tag, Search, Anchor, ArrowRight } from 'lucide-react';

const TABS = [
  { id: 'track', label: 'Track', icon: MapPin },
  { id: 'schedules', label: 'Schedules', icon: Calendar },
  { id: 'quote', label: 'Prices', icon: Tag },
];

function HeroField({ label, placeholder, icon: Icon, mono, value, onChange, testId }) {
  return (
    <label className="grid gap-2 flex-1 min-w-0">
      <span className="text-[13px] text-white/70">{label}</span>
      <span className="relative block">
        <Icon className="absolute left-3.5 top-1/2 -translate-y-1/2 h-[18px] w-[18px] text-gray-600" strokeWidth={1.75} />
        <input value={value} onChange={onChange} placeholder={placeholder} data-testid={testId}
          className={"w-full h-14 pl-11 pr-3.5 text-base text-ink bg-white border-0 rounded outline-none focus:ring-[3px] focus:ring-blue-500 " + (mono ? 'sv-mono' : '')} />
      </span>
    </label>
  );
}

export function Hero() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('track');
  const [q, setQ] = useState('');
  const [origin, setOrigin] = useState('');
  const [dest, setDest] = useState('');

  function go(e) {
    e.preventDefault();
    if (tab === 'track') { if (q.trim()) navigate('/track?ref=' + encodeURIComponent(q.trim().toUpperCase())); return; }
    const p = new URLSearchParams(); if (origin) p.set('origin', origin); if (dest) p.set('destination', dest);
    navigate((tab === 'schedules' ? '/schedules' : '/quote') + '?' + p.toString());
  }

  return (
    <section className="relative min-h-[620px] h-[calc(100vh-72px)] max-h-[860px] bg-ink" data-testid="hero-section">
      <img src="/img/sudvik-hero.jpg" alt="Container vessel alongside a terminal at dawn" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(to bottom, rgba(15,27,38,0.30) 0%, rgba(15,27,38,0.30) 40%, rgba(15,27,38,0.88) 100%)' }} />
      <div className="sv-wrap relative h-full grid grid-rows-[1fr_auto] pb-10 md:pb-14">
        <div className="self-center max-w-[760px] pt-10">
          <p className="text-[15px] text-white/75">Südvik Line · Container and bulk shipping since 1962</p>
          <h1 className="text-white sv-display text-[42px] sm:text-[52px] lg:text-[64px] mt-4">Every box, exactly where it should be.</h1>
          <p className="text-lg text-white/80 max-w-[560px] mt-5">Bulk, break-bulk and containers on one network of 260 ports — with schedule integrity we publish every week.</p>
        </div>
        <form onSubmit={go} className="bg-ink/60 backdrop-blur-sm border border-white/15 rounded p-5 md:p-6" data-testid="tracking-widget">
          <div className="flex gap-6 md:gap-8 border-b border-white/15">
            {TABS.map(function (t) {
              const is = t.id === tab;
              return (
                <button key={t.id} type="button" onClick={function () { setTab(t.id); }} data-testid={"hero-tab-" + t.id}
                  className={"inline-flex items-center gap-2 pb-3 -mb-px text-[15px] border-b-2 transition-colors duration-[120ms] " + (is ? 'text-white font-medium border-blue-500' : 'text-white/65 border-transparent hover:text-white')}>
                  <t.icon className="h-[18px] w-[18px]" strokeWidth={1.75} />{t.label}
                </button>
              );
            })}
          </div>
          <div className="flex flex-col md:flex-row gap-4 md:items-end mt-5">
            {tab === 'track' ? (
              <HeroField label="Container, booking or bill of lading number" placeholder="SDVU4829110-3 or SDV482911734" icon={Search} mono value={q} onChange={function (e) { setQ(e.target.value); }} testId="tracking-input" />
            ) : (
              <>
                <HeroField label={tab === 'schedules' ? 'From' : 'Origin'} placeholder="Port or city" icon={Anchor} value={origin} onChange={function (e) { setOrigin(e.target.value); }} testId="hero-origin-input" />
                <HeroField label={tab === 'schedules' ? 'To' : 'Destination'} placeholder="Port or city" icon={Anchor} value={dest} onChange={function (e) { setDest(e.target.value); }} testId="hero-destination-input" />
              </>
            )}
            <button type="submit" className="sv-btn sv-btn-brand h-14 px-7 shrink-0" data-testid={tab === 'track' ? 'track-btn' : 'hero-' + tab + '-btn'}>
              {tab === 'track' ? 'Track shipment' : tab === 'schedules' ? 'Find sailings' : 'Get prices'} <ArrowRight className="h-[18px] w-[18px]" strokeWidth={1.75} />
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
